import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Collection from 'assets/components/data/Collection';
import HeaderData from 'assets/components/data/rows/HeaderData';
import HeaderRow from 'assets/components/data/rows/HeaderRow';
import Row from 'assets/components/data/rows/Row';
import RowActions from 'assets/components/data/rows/RowActions';
import RowData from 'assets/components/data/rows/RowData';
import ThemeCollectionTable from 'assets/components/data/themes/Theme.Collection.Table';
import DataMap from 'assets/components/dataMap/DataMap';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales } from 'assets/locales/Locale';
import {
  defaultDateFormat,
  defaultDateTimeFormat,
  defaultServerDateTimeFormat,
  localizeDate,
} from 'assets/utils/data/Date';
import dayjs from 'dayjs';
import { sumBy, xorBy } from 'lodash';
import StatementStatusCode from 'models/enums/StatementStatusCode';
import { Fragment } from 'react';
import { useStatementProvider } from '../Statement.Hooks';

export default function StatementChangeStatus() {
  const { lang } = getLocales();
  const { setStatusApi, pageRouter, selected } = useStatementProvider();

  return (
    <ModalForm<Api.Core.Statement.SetStatus.IRequest>
      subcomponents={ThemeModalFormDefault}
      title={lang.statement}
      default={
        selected
          ? {
              statementId: selected.id,
              contractsToExclude: [],
            }
          : {}
      }
      onCancel={pageRouter.updateParams}
      onSubmit={setStatusApi.execute}
      validation={(data, errors) => {
        if (!data.statementStatus) errors.statementStatus = lang.mustNotBeEmpty;

        if (data.statementStatus === StatementStatusCode.RECONCILED) {
          if (!data.statementStatusChangeDate) {
            errors.statementStatusChangeDate = lang.mustNotBeEmpty;
          }
        }
      }}
      render={({ form }) => (
        <>
          <FormWrapper>
            <SelectInput
              name="statementStatus"
              label={lang.status}
              data={
                selected.status === StatementStatusCode.OPEN
                  ? [
                      {
                        id: StatementStatusCode.APPROVED,
                        title: lang.APPROVED,
                      },
                      {
                        id: StatementStatusCode.CANCELED,
                        title: lang.CANCELED,
                      },
                      {
                        id: StatementStatusCode.PRELIMINARY,
                        title: lang.PRELIMINARY,
                      },
                    ]
                  : selected.status === StatementStatusCode.APPROVED
                    ? [
                        {
                          id: StatementStatusCode.RECONCILED,
                          title: lang.RECONCILED,
                        },
                      ]
                    : []
              }
              value={form.data?.statementStatus}
              error={form.errors.statementStatus}
              onChange={(data) => {
                const nextStatus = (data as any).statementStatus;
                form.update(data);

                if (nextStatus === StatementStatusCode.RECONCILED && !form.data?.statementStatusChangeDate) {
                  form.update({
                    statementStatusChangeDate: dayjs().startOf('day').format(defaultServerDateTimeFormat),
                  });
                }
              }}
              icon={<Icon class="fas-check-double" />}
            />
            {form.data?.statementStatus === StatementStatusCode.RECONCILED && (
              <DateInput
                isLocal
                name="statementStatusChangeDate"
                label={lang.datePaymentReceived}
                error={form.errors.statementStatusChangeDate}
                dateTimeFormat={defaultDateFormat}
                min={dayjs().startOf('day').subtract(3, 'day').format(defaultServerDateTimeFormat)}
                max={dayjs().endOf('day').format(defaultServerDateTimeFormat)}
                value={form.data?.statementStatusChangeDate}
                onChange={form.update}
              />
            )}
          </FormWrapper>
          {selected.status === StatementStatusCode.OPEN && (
            <>
              <Collection class={ThemeCollectionTable} style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <HeaderRow active>
                  <HeaderData disableImage>{lang.exclusions}</HeaderData>
                  <HeaderData>{lang.priorTotalAmount}</HeaderData>
                  <HeaderData>{lang.newTotalAmount}</HeaderData>
                </HeaderRow>
                <Row>
                  <RowData>
                    <span>
                      {form.data.contractsToExclude?.length} / {selected?.contracts?.length}
                    </span>
                  </RowData>
                  <RowData>
                    <span>
                      <FormatValue
                        value={`${sumBy(selected?.contracts, (it) => it.paymentAmountWithTaxCAD).toFixed(2)}$`}
                      />
                    </span>
                  </RowData>
                  <RowData>
                    <span>
                      <FormatValue
                        value={`${(
                          sumBy(selected?.contracts, (it) => it.paymentAmountWithTaxCAD) -
                          sumBy(form.data.contractsToExclude, (it) => it.paymentAmountWithTaxCAD)
                        ).toFixed(2)}$`}
                      />
                    </span>
                  </RowData>
                </Row>
                <HeaderRow>
                  <HeaderData disableImage>{lang.contract}</HeaderData>
                  <HeaderData>{lang.term}</HeaderData>
                  <HeaderData>{lang.client}</HeaderData>
                  <HeaderData>{lang.amount}</HeaderData>
                  <HeaderData>{lang.tax}</HeaderData>
                  <HeaderData>{lang.total}</HeaderData>
                  <HeaderData numOfActions={1} />
                </HeaderRow>
              </Collection>
              <Collection class={ThemeCollectionTable}>
                <DataMap
                  data={selected.contracts}
                  render={({ data: contract }) => (
                    <Fragment key={contract.id}>
                      <Row>
                        <RowData
                          info={
                            contract.statusCode
                              ? lang[contract.statusCode]?.replace(/_/g, ' - ')
                              : lang.noContractDefined
                          }
                          title={contract.appNumberWithRevision}
                          subtitle={contract.product?.name}
                        />
                        <RowData>
                          <FormatValue value={contract.termInMonths} />
                        </RowData>
                        <RowData>{contract.client?.name}</RowData>
                        <RowData>
                          <span>
                            <FormatValue value={`${contract.paymentAmountCAD.toFixed(2)}$`} />
                          </span>
                        </RowData>
                        <RowData>
                          <span>
                            <FormatValue value={`${contract.paymentTaxCAD.toFixed(2)}$`} />
                          </span>
                        </RowData>
                        <RowData>
                          <span>
                            <FormatValue value={`${contract.paymentAmountWithTaxCAD.toFixed(2)}$`} />
                          </span>
                        </RowData>
                        <RowActions isVertical>
                          <Button
                            class={ThemeButtonCircle}
                            media={
                              <Icon
                                class={
                                  form.data.contractsToExclude?.some((c) => c.id === contract.id)
                                    ? 'fas-check-square'
                                    : 'far-square'
                                }
                              />
                            }
                            onClick={() => {
                              form.update({
                                contractsToExclude: xorBy([{ ...contract }], form.data.contractsToExclude, (c) => c.id),
                              });
                            }}
                            htmlElementProps={{
                              title: form.data.contractsToExclude?.some((c) => c.id === contract.id)
                                ? lang.unselect
                                : lang.select,
                            }}
                          />
                        </RowActions>
                      </Row>
                      <Row isCompressed>
                        <RowData disableAutoAlignment style={{ alignItems: 'flex-end' }}>
                          <span>
                            <b>{lang.createdOn}&nbsp;</b>
                            <span>
                              {localizeDate(contract?.createdAtLocal, defaultDateTimeFormat)}
                              &nbsp;{lang.by}&nbsp;
                              {contract.createdBy?.fullName || lang.unknownUser}
                              .&nbsp;
                            </span>
                            <b>{lang.effectiveFrom}&nbsp;</b>
                            <span>{localizeDate(contract?.effectiveDate, defaultDateFormat)}</span>
                          </span>
                        </RowData>
                      </Row>
                    </Fragment>
                  )}
                />
              </Collection>
            </>
          )}
        </>
      )}
    />
  );
}
